import { useAuthenticator } from '@aws-amplify/ui-react-core';
import { I18n } from 'aws-amplify/utils';
import {
  type AuthError,
  type SignOutInput,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  confirmUserAttribute,
  resendSignUpCode,
  resetPassword,
  sendUserAttributeVerificationCode,
  setUpTOTP,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  updateUserAttributes,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import posthog from 'posthog-js';
import {
  type ReactNode,
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  FEDERATED_SESSION_STORAGE_KEY,
  FEDERATED_SIGNED_OUT_STORAGE_KEY,
  isFederatedSessionSignedOut,
  parseFederatedSessionToken,
  readFederatedSession,
  signOutFederatedSession,
} from '../../federated-session';
import { exitImpersonation, readImpersonationSession } from '../../impersonation';
import { clearBearerTokenInterceptor } from './useAddBearerTokenInAxios';

export type AuthStatus = 'configuring' | 'authenticated' | 'unauthenticated';

export enum IdentityProviders {
  GOOGLE = 'Google',
  APPLE = 'Apple',
  FACEBOOK = 'Facebook',
}

export type * from 'aws-amplify/auth';

export { AuthError } from 'aws-amplify/auth';

export type UseAuthReturnType = {
  authStatus: AuthStatus;
};

const AuthStatusContext = createContext<AuthStatus>('unauthenticated');

/** Exposes Amplify's status; only rendered inside Amplify's `Authenticator.Provider`. */
export const AmplifyAuthStatusProvider = ({ children }: { children: ReactNode }) => {
  const { authStatus } = useAuthenticator();
  return createElement(AuthStatusContext.Provider, { value: authStatus }, children);
};

const subscribeToStorage = (onChange: () => void) => {
  window.addEventListener('storage', onChange);
  return () => window.removeEventListener('storage', onChange);
};

/**
 * Status of a federated login app. Follows the other tabs: a sign-out there signs this tab out,
 * and a login as another broker (connect-as) reloads this tab into that broker's context.
 */
export const FederatedAuthStatusProvider = ({ children }: { children: ReactNode }) => {
  const brokerUserId = useSyncExternalStore(
    subscribeToStorage,
    () => readFederatedSession()?.brokerUserId ?? null,
  );
  const [initialBrokerUserId] = useState(brokerUserId);

  useEffect(() => {
    const onStorage = ({ key, newValue }: StorageEvent) => {
      if (key === FEDERATED_SIGNED_OUT_STORAGE_KEY && newValue !== null) {
        window.location.replace('/auth/refused?reason=signed_out');
      }
      if (key === FEDERATED_SESSION_STORAGE_KEY && newValue && initialBrokerUserId) {
        const next = parseFederatedSessionToken(newValue);
        if (next && next.brokerUserId !== initialBrokerUserId) window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [initialBrokerUserId]);

  return createElement(
    AuthStatusContext.Provider,
    { value: brokerUserId ? 'authenticated' : 'unauthenticated' },
    children,
  );
};

export const useAuth = (): UseAuthReturnType => ({ authStatus: useContext(AuthStatusContext) });

type Options = {
  // biome-ignore lint/suspicious/noExplicitAny: We use 'any' here to allow contravariance in callback parameter types
  onSuccess?: (result: any) => void;
  onError?: (e: AuthError) => void;
};

// Resolves to undefined when the action failed; the error is exposed via `errorMessage`/`onError`.
type AuthAction<T extends (...args: never[]) => unknown> = (
  ...args: Parameters<T>
) => Promise<Awaited<ReturnType<T>> | undefined>;

export const useAuthActionHook = <T extends (...args: Parameters<T>) => ReturnType<T>>(
  action: T,
  options?: Options,
) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const newAction = async (...args: Parameters<T>) => {
    setIsLoading(true);
    try {
      const result = await action(...args);
      options?.onSuccess?.(result);
      return result;
    } catch (e) {
      const error = e as AuthError;
      options?.onError?.(error);

      setErrorMessage(I18n.get(error.message));
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, action: newAction as AuthAction<T>, errorMessage };
};

type AuthHook<T extends (...args: never[]) => unknown, Name extends string> = (
  options?: Options,
) => {
  isLoading: boolean;
  errorMessage?: string;
} & {
  [K in Name]: AuthAction<T>;
};

export const useSignInWithRedirect = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(signInWithRedirect, {
    ...options,
    onSuccess: result => {
      options?.onSuccess?.(result);
      if (result && typeof result === 'object' && 'userId' in result) {
        posthog.identify((result as { userId: string }).userId);
      }
    },
  });
  return { ...rest, signInWithRedirect: action };
};
export const useSignIn: AuthHook<typeof signIn, 'signIn'> = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(signIn, {
    ...options,
    onSuccess: result => {
      options?.onSuccess?.(result);
      if (result && typeof result === 'object' && 'userId' in result) {
        posthog.identify((result as { userId: string }).userId);
      }
    },
  });
  return { ...rest, signIn: action };
};
/**
 * Under a Korint-staff impersonation session there is no Amplify session to sign
 * out of — Amplify's `signOut` would resolve and leave the operator apparently
 * signed in forever. Ending the impersonation session is the equivalent action.
 */
const signOutOrExitImpersonation = async (input?: SignOutInput): Promise<void> => {
  if (readImpersonationSession()) {
    return exitImpersonation();
  }
  if (readFederatedSession() || isFederatedSessionSignedOut()) {
    return signOutFederatedSession();
  }
  return signOut(input);
};

export const useSignOut = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(signOutOrExitImpersonation, {
    ...options,
    onSuccess: result => {
      options?.onSuccess?.(result);
      posthog.reset();
      clearBearerTokenInterceptor();
    },
  });
  return { ...rest, signOut: action };
};

export const useSignUp = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(signUp, options);
  return { ...rest, signUp: action };
};

export const useResetPassword = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(resetPassword, options);
  return { ...rest, resetPassword: action };
};

export const useResendSignUpCode = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(resendSignUpCode, options);
  return { ...rest, resendSignUpCode: action };
};

export const useConfirmSignIn = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(confirmSignIn, options);
  return { ...rest, confirmSignIn: action };
};

export const useConfirmSignUp = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(confirmSignUp, options);
  return { ...rest, confirmSignUp: action };
};

export const useConfirmResetPassword = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(confirmResetPassword, options);
  return { ...rest, confirmResetPassword: action };
};

export const useUpdatePassword = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(updatePassword, options);
  return { ...rest, updatePassword: action };
};

export const useSetUpTotp = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(setUpTOTP, options);
  return { ...rest, setUpTOTP: action };
};

export const useVerifyTotpSetup = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(verifyTOTPSetup, options);
  return { ...rest, verifyTOTPSetup: action };
};

export const useUpdateMfaPreference = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(updateMFAPreference, options);
  return { ...rest, updateMFAPreference: action };
};

export const useUpdateUserAttributes = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(updateUserAttributes, options);
  return { ...rest, updateUserAttributes: action };
};

export const useSendUserAttributeVerificationCode = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(sendUserAttributeVerificationCode, options);
  return { ...rest, sendUserAttributeVerificationCode: action };
};

export const useConfirmUserAttribute = (options?: Options) => {
  const { action, ...rest } = useAuthActionHook(confirmUserAttribute, options);
  return { ...rest, confirmUserAttribute: action };
};
